import * as React from 'react'

import { BoxIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { IAttachment } from '~/client/graph'
import { CheckCircleFillIcon } from '~/client/src/shared/components/icons/CheckCircleFillIcon'
import { ImportantAtIcon } from '~/client/src/shared/components/icons/ImportantAtIcon'
import { ImportantBellIcon } from '~/client/src/shared/components/icons/ImportantBellIcon'
import { ImportantBicIcon } from '~/client/src/shared/components/icons/ImportantBicIcon'
import { StateUnfilledCheckboxRadioIcon } from '~/client/src/shared/components/icons/StateUnfilledCheckboxRadioIcon'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { SHOW_FULLSCREEN_PREVIEW } from '~/client/src/shared/stores/EventStore/eventConstants'
import { openAttachmentPreview } from '~/client/src/shared/utils/filePreviewUtils'
import { isEventFromOwnDom } from '~/client/src/shared/utils/isEventFromOwnDom'

import Workflow from '../models/Workflow'
import CompaniesStore from '../stores/domain/Companies.store'
import LocationAttributesStore from '../stores/domain/LocationAttributes.store'
import MaterialsStore from '../stores/domain/Materials.store'
import ProjectMembersStore from '../stores/domain/ProjectMembers.store'
import TagsStore from '../stores/domain/Tags.store'
import EventsStore from '../stores/EventStore/Events.store'
import ProjectDateStore from '../stores/ui/ProjectDate.store'
import { cn } from '../utils/cn'
import { INTERPUNCT } from '../utils/usefulStrings'
import Avatar, { AvatarSize } from './Avatar/Avatar'
import AttachmentsPreviewStrip from './FilesInput/AttachmentsPreviewStrip'
import SitemapAttributeTag from './SitemapAttributeTag/SitemapAttributeTag'
import WorkflowBacklinks from './WorkflowBacklinks/WorkflowBacklinks'

import './WorkflowCard/WorkflowCard.scss'

interface IProps {
  permit: Workflow
  className?: string
  onClick?: (permit: Workflow) => void
  projectDateStore?: ProjectDateStore
  workflowTypesStore?: WorkflowTypesStore
  isSelected?: boolean
  isSelectionMode?: boolean
  companiesStore?: CompaniesStore
  eventsStore?: EventsStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  tagsStore?: TagsStore
  materialsStore?: MaterialsStore
  isBic?: boolean
  importantKind?: 'bic' | 'announcement' | 'mention' | null
  displayOnlyBacklinks?: boolean
  onBacklinksWorkflowClick?: (workflow: Workflow) => void
  hideBreadcrumbs?: boolean
}

// The location row below draws a 16px icon 4px in from the edge, so the avatars match it. The
// legacy size class is unlayered, hence the bang.
const ROW_AVATAR_CLASS = 'tw:size-4! tw:mx-1!'

@inject(
  'projectDateStore',
  'workflowTypesStore',
  'companiesStore',
  'projectMembersStore',
  'locationAttributesStore',
  'tagsStore',
  'materialsStore',
  'eventsStore',
)
@observer
export default class PermitListItem extends React.Component<IProps> {
  public render() {
    const { className } = this.props

    return (
      <div
        key={this.props.permit.id}
        className={cn({
          'tw:py-3 workflow-card text large overflow-hidden': true,
          'tw:cursor-pointer': !!this.props.onClick,
          'tw:cursor-default': !this.props.onClick,
          [className]: !!className,
        })}
        onClick={e => {
          if (isEventFromOwnDom(e)) this.onClick()
        }}
      >
        {this.renderHeading()}
        {this.renderCompanyAndUserRow()}

        {this.renderLocationsRow()}
        {this.renderEquipmentAndMaterialsRow()}
        {this.renderAttachmentsPreviewRow()}
      </div>
    )
  }

  private renderHeading() {
    const {
      permit: {
        getIconNameOfPermitType,
        getNameOfPermitType,
        status,
        getCaption,
        isLate,
        workflowStepLevel,
        startDate,
        endDate,
      },
      workflowTypesStore,
      projectDateStore,
    } = this.props
    const { permit, isBic, importantKind, isSelectionMode, isSelected } =
      this.props
    const compactDate = projectDateStore.getCompactDateTimeInterval(
      startDate,
      endDate,
    )

    return (
      <>
        <div className="extra-large pt5 row align-start">
          {isSelectionMode ? (
            <span className="no-grow no-shrink mr8 row x-center y-center">
              {isSelected ? (
                <CheckCircleFillIcon size="md" />
              ) : (
                <StateUnfilledCheckboxRadioIcon
                  size="md"
                  className="tw:text-muted-foreground"
                />
              )}
            </span>
          ) : (
            <WorkflowTypeIcon
              iconName={getIconNameOfPermitType(workflowTypesStore)}
              size="small"
              className="tw:mr-2"
            />
          )}
          <span
            title={getCaption(workflowTypesStore)}
            className="text extra-large bold line-clamp"
          >
            {getCaption(workflowTypesStore)}
          </span>
          <div className="row no-grow no-shrink ml-auto y-start">
            <WorkflowBacklinks
              workflow={permit}
              onWorkflowClick={
                this.props.onBacklinksWorkflowClick || this.onClick
              }
              displayOnly={this.props.displayOnlyBacklinks}
            />
            <StatusBadge
              className="tw:!flex-none"
              status={status}
              workflowStepLevel={workflowStepLevel}
              isLate={isLate}
            />
            {this.renderImportantIcon(importantKind ?? (isBic ? 'bic' : null))}
          </div>
        </div>
        <div className="pl30 row x-start">
          <span
            className="text grey-light text-ellipsis min-width0 flex-unset"
            title={getNameOfPermitType(workflowTypesStore)}
          >
            {getNameOfPermitType(workflowTypesStore)}
          </span>
          <span className="no-grow px4 text large grey-light">
            {INTERPUNCT}
          </span>
          <span
            className="text grey-light text-ellipsis min-width0 flex-unset"
            title={compactDate}
          >
            {compactDate}
          </span>
        </div>
      </>
    )
  }

  private renderCompanyAndUserRow() {
    const { companyIds, responsibleIds } = this.props.permit
    const { getCompanyById } = this.props.companiesStore
    const { getByIds } = this.props.projectMembersStore
    const companies = companyIds?.map(id => getCompanyById(id))
    const responsibles = getByIds(responsibleIds)

    if (!companies?.length && !responsibles?.length) {
      return null
    }

    return (
      <div className="pt8 pl25 row wrap">
        {companies?.map(company => (
          <React.Fragment key={company.id}>
            {company.avatarUrl || company.logoUrl ? (
              <Avatar
                src={company.avatarUrl || company.logoUrl}
                size={AvatarSize.XMinuscule}
                className={ROW_AVATAR_CLASS}
              />
            ) : (
              // A company with no logo is a building, as on its map label, not a person.
              <span className="tw:mx-1 tw:size-4 tw:flex-none!" aria-hidden>
                {renderResourceSemanticIcon('company')}
              </span>
            )}
            <span className="text medium-bold no-grow no-white-space-wrap pr4">
              {company.name}
            </span>
          </React.Fragment>
        ))}
        {companies?.length > 0 && responsibles?.length > 0 && (
          <span className="no-grow">{INTERPUNCT}</span>
        )}
        {responsibles?.map(responsible => (
          <React.Fragment key={responsible.id}>
            <Avatar
              src={responsible.avatarUrl}
              size={AvatarSize.XMinuscule}
              className={ROW_AVATAR_CLASS}
            />
            <span className="text medium-bold no-grow no-white-space-wrap">
              {responsible.fullName}
            </span>
          </React.Fragment>
        ))}
      </div>
    )
  }

  private renderLocationsRow(): JSX.Element {
    const {
      permit: { locations },
      locationAttributesStore: { getById },
      tagsStore: { tagStoreByTagTypeMap },
    } = this.props

    if (!locations.length) {
      return null
    }
    return (
      <div className="nowrap pl25 pt4">
        {locations
          .map(l => getById(l.id))
          .filter(dto => !!dto)
          .map(dto => {
            const hierarchy = this.props.hideBreadcrumbs
              ? []
              : dto.getHierarchyChainObjs(tagStoreByTagTypeMap)
            const hierarchyNames = `${INTERPUNCT} ${hierarchy
              .map(h => h.name)
              .join(` ${INTERPUNCT} `)}`
            const title = this.props.hideBreadcrumbs
              ? dto.name
              : `${dto.name} ${hierarchyNames}`

            return (
              <div
                key={dto.id}
                title={title}
                className="nowrap text-ellipsis row unset-flex-children-direct"
                style={{ color: dto.color }}
              >
                <SitemapAttributeTag
                  contentContainerClassName="text-ellipsis"
                  dataObject={dto}
                  shouldShowAsTag={false}
                >
                  {dto.name}
                </SitemapAttributeTag>
                {hierarchy.length > 0 && (
                  <span className="text light line-large text-ellipsis">
                    {hierarchyNames}
                  </span>
                )}
              </div>
            )
          })}
      </div>
    )
  }

  private renderEquipmentAndMaterialsRow(): JSX.Element {
    const {
      permit: { equipment, materials },
      locationAttributesStore: { getById: getLocationById },
      materialsStore: { getByIds: getMaterialsByIds },
    } = this.props

    if (!equipment.length && !materials.length) {
      return null
    }

    const materialList = getMaterialsByIds(materials.map(m => m.materialId))

    const equipmentDtos = equipment
      .map(e => getLocationById(e.id))
      .filter(dto => !!dto)

    const combinedItems = [
      ...equipmentDtos.map(dto => ({
        key: `eq-${dto.id}`,
        node: (
          <div
            title={dto.name}
            className="nowrap no-grow"
            style={{ color: dto.color }}
          >
            <SitemapAttributeTag dataObject={dto} shouldShowAsTag={false}>
              {dto.name}
            </SitemapAttributeTag>
          </div>
        ),
      })),
      ...materialList.map(material => ({
        key: `mat-${material.id}`,
        node: (
          <div title={material.productName} className="no-grow">
            <span className="text large line-large no-grow no-white-space-wrap tw:pl-1">
              <BoxIcon className="tw:inline-block tw:w-4 tw:h-4 tw:mr-1 tw:align-middle" />
              {material.productName}
            </span>
          </div>
        ),
      })),
    ]

    return (
      <>
        <div className="pl24 row align-start wrap">
          {combinedItems.map(({ key, node }, index) => (
            <React.Fragment key={key}>
              {index > 0 && <span className="no-grow px4">{INTERPUNCT}</span>}
              {node}
            </React.Fragment>
          ))}
        </div>
      </>
    )
  }

  private showPreview = (files: IFilePreviewProperties[], index: number) => {
    this.props.eventsStore?.dispatch(SHOW_FULLSCREEN_PREVIEW, files, index)
  }

  private onPreview = (attachments: IAttachment[], index: number) => {
    openAttachmentPreview(attachments, index, this.showPreview)
  }

  private renderAttachmentsPreviewRow() {
    const { permit } = this.props
    const attachments = permit.attachments || []
    if (!attachments.length) {
      return null
    }

    return (
      <div className="tw:pl-[25px] tw:pt-2">
        <AttachmentsPreviewStrip
          attachments={attachments}
          maxPreviews={3}
          onPreview={this.onPreview}
        />
      </div>
    )
  }

  private renderImportantIcon(
    kind: 'bic' | 'announcement' | 'mention' | null,
  ): JSX.Element {
    switch (kind) {
      case 'bic':
        return (
          <span className="row x-center y-center no-grow no-shrink w20 h20 ml4">
            <ImportantBicIcon size="sm" />
          </span>
        )
      case 'announcement':
        return (
          <span className="row x-center y-center no-grow no-shrink w20 h20 ml4">
            <ImportantBellIcon size="sm" />
          </span>
        )
      case 'mention':
        return (
          <span className="row x-center y-center no-grow no-shrink w20 h20 ml4">
            <ImportantAtIcon size="sm" />
          </span>
        )
      default:
        return null
    }
  }

  private onClick = (permit: Workflow = null) => {
    const { onClick } = this.props
    if (permit) {
      onClick?.(permit)
    } else {
      onClick?.(this.props.permit)
    }
  }
}
